import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type {
  ReportFilters,
  ReportQueryParams,
} from '@/features/reports/lib/report-filters'
import { apiRequest } from '@/api/client'
import {
  buildReportParams,
  buildZeroSellerParams,
} from '@/features/reports/lib/report-filters'

/**
 * The one reporting endpoint (CHG-2026-09-manager-reporting, D9).
 *
 * Every breakdown on the dashboard and on the report page is this hook with a different
 * `dimension`. That is why the dashboard's tiles and the tables they drill into can never disagree:
 * they are the same query, cached under the same key.
 */
export type ReportDimension =
  | 'time_week'
  | 'time_day'
  | 'time_hour'
  | 'weekday_hour'
  | 'daypart'
  | 'channel'
  | 'category_main'
  | 'category_sub'
  | 'article'
  | 'payment_method'
  | 'restaurant'

export type ReportMeasure = 'revenue' | 'units'

export interface ReportTotals {
  revenueCents: number
  units: number
  bons: number
  skus: number
}

export interface ReportRow {
  key: string
  label: string | null
  parentKey: string | null
  parentLabel: string | null
  revenueCents: number
  units: number
  /**
   * Distinct receipts containing at least one item of this row. A bon spanning two product groups
   * counts in both, so this column does not sum to `total.bons` — that one counts each receipt once.
   */
  bons: number
  skus: number
  previousRevenueCents: number | null
  previousUnits: number | null
  isCollector: boolean
}

/** Advisory notes the UI must render. Codes, so the wording is translated, not sent. */
export interface ReportCaveat {
  code: string
  since: string | null
}

export interface SalesReport {
  dimension: ReportDimension
  measure: ReportMeasure
  comparison: string
  from: string
  to: string
  previousFrom: string
  previousTo: string
  total: ReportTotals
  previous: ReportTotals
  totalRows: number
  rows: Array<ReportRow>
  caveats: Array<ReportCaveat>
}

export interface ZeroSellerRow {
  sku: string
  name: string | null
  categoryLabel: string | null
  priceCents: number | null
  lastSoldAt: string | null
}

export interface ZeroSellers {
  from: string
  to: string
  total: number
  rows: Array<ZeroSellerRow>
  caveats: Array<ReportCaveat>
}

/**
 * A report is a snapshot the reader refreshes deliberately, so it does not go stale on its own —
 * the same reasoning as the pickup report. What it must not do is flash: the caller holds the
 * previous render at reduced opacity while a new slice loads.
 */
const REPORT_STALE_TIME = 60_000

export function useSalesReportQuery(params: ReportQueryParams, enabled = true) {
  const qs = buildReportParams(params, new Date())
  return useQuery({
    queryKey: ['sales-report', qs],
    queryFn: () => apiRequest<SalesReport>(`/api/v1/admin/reports/sales?${qs}`),
    staleTime: REPORT_STALE_TIME,
    // The running day's window ends at the current minute, so the key changes as the clock moves
    // and every one of those keys is one nobody has fetched before. Without this the dashboard
    // dropped to its skeletons on each tick — `isLoading` is true for a key with no data, however
    // fresh the figures on screen still are.
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useZeroSellersQuery(
  filters: ReportFilters,
  paging?: { limit?: number; offset?: number },
  enabled = true,
) {
  const qs = buildZeroSellerParams(filters, new Date(), paging)
  return useQuery({
    queryKey: ['zero-sellers', qs],
    queryFn: () =>
      apiRequest<ZeroSellers>(`/api/v1/admin/reports/zero-sellers?${qs}`),
    staleTime: REPORT_STALE_TIME,
    enabled,
  })
}
